import type { Locale } from '@sentra/contracts';
import { DEFAULT_LOCALE, isLocale, LOCALES } from '@sentra/contracts';
import type { i18n as I18n } from 'i18next';
import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import { en } from './en';
import type { OperatorCatalogue } from './es';
import { es } from './es';

/**
 * The browser's translation instance, its two resource bundles, and where the operator's choice of
 * language is remembered.
 *
 * **Spanish is the default and it is not a fallback.** `fallbackLng` is `false` on purpose: with a
 * fallback, a key missing from English renders the Spanish sentence and nobody finds out, which is
 * exactly the drift `test:i18n` exists to refuse. Without one, a missing key renders as the key,
 * which is ugly and immediately visible — the same trade `@sentra/contracts`'s `interpolate` makes
 * when it leaves an unsubstituted `{{minutes}}` standing rather than blanking it.
 *
 * **`initAsync` is off.** i18next 26 defaults it on, which makes `t()` return the key until a
 * microtask has run; every resource here is a plain object that is already in memory, so there is
 * nothing to wait for, and waiting would mean the first paint of every screen shows its key names.
 *
 * **`escapeValue` is off, and that is correct rather than convenient.** React escapes everything it
 * renders as a child, so i18next's own escaping would double-escape an apostrophe. The rule that
 * makes this safe is that **no translated string is ever rendered as HTML**: the one
 * `dangerouslySetInnerHTML` in this package renders the authenticator QR that `qr` encodes from the
 * server's own enrolment URI, and `shell.web.test.tsx` scans the package and fails if a second one
 * appears.
 *
 * **The instance is created rather than taken from the module singleton.** A test renders several
 * trees in one process, and a shared singleton makes the language one test chose leak into the
 * next; `createI18n` hands each tree its own.
 */

/** The one namespace this application uses. Error messages come from the contracts catalogue. */
export const UI_NAMESPACE = 'ui';

/** Where the operator's language choice is remembered on this terminal. */
export const LOCALE_STORAGE_KEY = 'sentra.locale';

/**
 * The resources this instance starts with: the operator half of each catalogue.
 *
 * **The administration half is added to this same namespace when that section loads**, by
 * `features/admin/catalogue.ts`, because it travels in the administration chunk rather than in the
 * bytes every terminal downloads to reach a gate. That is why the value here is
 * `OperatorCatalogue` and the type i18next is told about is the union of both halves: what is in
 * memory at boot is one half, and what a component may legitimately name is either.
 */
function operatorResources(): Record<Locale, Record<string, OperatorCatalogue>> {
  // **A fresh object per instance, because i18next's store keeps what it is given BY REFERENCE and
  // `addResourceBundle` deep-extends into it.** Shared, the administration bundle that
  // `features/admin/catalogue.ts` adds to the first tree would be written into these module-level
  // catalogues and every later `createI18n()` would start with `admin.*` already present — which
  // makes the `WeakSet` guard over there read as pointless when it is not, and is order-dependent
  // shared state in a suite that builds several instances per process. The spread is shallow and
  // that is enough: the merge only ever adds `admin`, a member `es.ts` does not declare.
  return { es: { [UI_NAMESPACE]: { ...es } }, en: { [UI_NAMESPACE]: { ...en } } };
}

/** A fresh, fully initialised instance in one language. */
export function createI18n(locale: Locale): I18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    lng: locale,
    supportedLngs: [...LOCALES],
    resources: operatorResources(),
    ns: [UI_NAMESPACE],
    defaultNS: UI_NAMESPACE,
    fallbackLng: false,
    initAsync: false,
    returnNull: false,
    interpolation: { escapeValue: false },
  });
  return instance;
}

/**
 * The language remembered on this terminal, or `null` when none is.
 *
 * A storage that throws — a browser with site data disabled, or a private window in some engines —
 * is treated as "nothing remembered" rather than allowed to break the sign-in page. Language is a
 * preference; losing it costs one click and taking the application down with it costs a shift.
 */
export function storedLocale(storage: Pick<Storage, 'getItem'>): Locale | null {
  let raw: string | null;
  try {
    raw = storage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
  return raw !== null && isLocale(raw) ? raw : null;
}

/** Remembers a language on this terminal, ignoring a storage that refuses to keep it. */
function rememberLocale(storage: Pick<Storage, 'setItem'>, locale: Locale): void {
  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // See `storedLocale`: a preference that cannot be persisted is still a preference.
  }
}

/**
 * The language this session starts in: what the terminal remembers, then what the installation
 * declares, then Spanish.
 *
 * The installation's default arrives from `GET /api/v1/config`, which is fetched before the first
 * render, so there is never a moment where the application is showing one language and about to
 * switch to another.
 */
export function initialLocale(
  storage: Pick<Storage, 'getItem'>,
  installationDefault: Locale = DEFAULT_LOCALE,
): Locale {
  return storedLocale(storage) ?? installationDefault;
}

/** The active language, narrowed back to the two this product ships. */
export function activeLocale(instance: Pick<I18n, 'language'>): Locale {
  return isLocale(instance.language) ? instance.language : DEFAULT_LOCALE;
}

/**
 * Declares the document's language, which is a different thing from rendering in it.
 *
 * `index.html` ships `lang="es"` and the operator can switch to English, so without this the
 * document keeps declaring Spanish while every string on it is English: a screen reader then reads
 * English prose with a Spanish voice and a speech engine applies Spanish pronunciation rules to it,
 * which is WCAG 3.1.1's whole subject. Nothing catches it by looking — the screen is correct — and
 * axe cannot catch it either, because a document that declares A language passes `html-has-lang`
 * whatever its content is. The end-to-end language walk asserts it instead.
 *
 * It takes the element rather than reaching for `document`, for the reason `applyTheme` does: the
 * same function has to be callable before the tree is mounted and from inside a test's own document.
 */
export function applyDocumentLocale(locale: Locale, element: HTMLElement): void {
  // `setAttribute` rather than `element.lang = locale`, which is a property write on a parameter:
  // `no-param-reassign` stays at `props: true` everywhere but the two Express middlewares that are
  // named in the configuration, and widening that exception for one attribute would be trading a
  // rule for a line.
  element.setAttribute('lang', locale);
}

/**
 * The active language and the one way to change it.
 *
 * Changing it here is what makes "switching language changes every visible string with no reload"
 * true: every string in the tree comes from `t()`, so i18next's own re-render is the whole of the
 * mechanism. The choice is written to storage in the same call, because a language that resets on
 * the next shift is not a preference.
 */
export function useLocale(): { locale: Locale; setLocale: (next: Locale) => void } {
  const { i18n } = useTranslation(UI_NAMESPACE);
  return {
    locale: activeLocale(i18n),
    setLocale: (next: Locale) => {
      rememberLocale(globalThis.localStorage, next);
      applyDocumentLocale(next, globalThis.document.documentElement);
      void i18n.changeLanguage(next);
    },
  };
}
